import { useState, useEffect } from 'react'

/** Font Awesome icon style prefixes */
type FaStyle = 'fas' | 'far' | 'fab'

/**
 * Props for the DynamicFaIcon component.
 * @interface Props
 * @extends {React.HTMLAttributes<HTMLSpanElement>} - Inherits standard HTML span attributes
 */
interface Props extends React.HTMLAttributes<HTMLSpanElement> {
  /** Font Awesome icon name in kebab-case (e.g., "coffee", "address-book") */
  iconName?: string
  /**
   * Style prefix for the icon
   * - fas: solid (default)
   * - far: regular
   * - fab: brands
   */
  stylePrefix?: FaStyle
  /** Font Awesome version to use (defaults to FONT_AWESOME_VERSION const) */
  version?: string
  /** Additional CSS class names to apply to the icon container */
  className?: string
  /** Placeholder content to display while loading or if icon fails to load */
  fallback?: React.ReactNode
}

/** Default Font Awesome version */
const FONT_AWESOME_VERSION = '7.0.0'

/** Base CDN URL for Font Awesome assets */
const FONT_AWESOME_CDN_URL = 'https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free'

/**
 * A component that dynamically loads and displays Font Awesome icons at runtime.
 * Fetches SVG content from CDN and renders it inline.
 *
 * @param {Props} props Component properties
 * @returns {React.ReactNode} Rendered icon or fallback content
 * @story User Story 11431: AI generated icon for assessment option
 */
export function DynamicFaIcon({
  iconName,
  stylePrefix = 'fas',
  version = FONT_AWESOME_VERSION,
  className,
  fallback,
}: Props) {
  const [svg, setSvg] = useState<string | null>(null)
  const [isFailed, setIsFailed] = useState(false)

  useEffect(() => {
    let cancelled = false

    if (!iconName) {
      setIsFailed(true)
      return
    }

    // Validate iconName: remove style prefix ('fas ' etc.), 'fa-' prefix if present
    let normalizedIconName = iconName.replace(/^.+\s+/, '')
    normalizedIconName = normalizedIconName.startsWith('fa-') ? normalizedIconName.slice(3) : normalizedIconName

    // Build the URL based on style prefix, version, and icon name
    const styleDir = stylePrefix === 'far' ? 'regular' : stylePrefix === 'fab' ? 'brands' : 'solid'
    // cspell:ignore svgs
    const url = `${FONT_AWESOME_CDN_URL}@${version}/svgs/${styleDir}/${normalizedIconName}.svg`

    // Load the SVG from the CDN
    fetch(url)
      .then(resp => (resp.ok ? resp.text() : Promise.reject(resp.status)))
      .then(svgString => {
        if (cancelled) return
        setSvg(svgString)
        setIsFailed(false)
      })
      .catch(err => {
        if (cancelled) return
        console.warn(`Could not load FA SVG ${normalizedIconName}`, err)
        setIsFailed(true)
      })

    return () => {
      cancelled = true
    }
  }, [iconName, stylePrefix, version])

  // Return fallback content if loading failed
  if (isFailed) return fallback

  // Return null while loading
  if (!svg) return null

  return (
    <span
      className={`flex size-4.5 [&>svg]:size-4.5 justify-center items-center ${className}`}
      dangerouslySetInnerHTML={{ __html: svg }}
    ></span>
  )
}
